import {
  Box,
  Button,
  Icon,
  IconButton,
  Icons,
  Stack,
  Tooltip,
  Typography,
} from '@nextiva/next-ui-components';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { FileAttachments } from 'interface/WizardV3';
import { AttachmentType } from 'services';
import {
  StyledIcon,
  UploadedFile,
  UploadGrid,
  VisuallyHiddenInput,
} from './Steps.styled';
import { useWizardContextV3 } from './WizardContextV3/WizardContextV3';
import { Subtitle3Typo } from './WizardV3.styled';

const dataTestId = 'PlatformAdminUI-FileUpload';

export type SelectedFileProps = {
  file: FileAttachments;
  onClick: (fileId: string) => void;
};

export const SelectedFile: FC<SelectedFileProps> = ({ file, onClick }) => {
  const [onHover, setOnHover] = useState(false);

  return (
    <Tooltip key={file.fileName} title={file.fileName} placement="top-start">
      <UploadedFile
        direction="row"
        spacing="8px"
        sx={{ background: onHover ? '#F5FAFF' : '' }}
        onMouseEnter={() => setOnHover(true)}
        onMouseLeave={() => setOnHover(false)}
      >
        <Stack
          justifyContent="center"
          alignItems="center"
          sx={{
            minWidth: '40px',
            height: '40px',
            borderRadius: '50%',
            background: onHover ? '#F5FAFF' : '',
          }}
        >
          <Icon
            icon={Icons.fileAlt}
            size={24}
            sx={{ color: onHover ? 'blue.900' : '' }}
          />
        </Stack>
        <Typography
          variant="button"
          fontSize="12px"
          sx={{
            textOverflow: 'ellipsis',
            overflow: 'hidden',
            whiteSpace: 'nowrap',
            alignSelf: 'center',
          }}
        >
          {file.fileName}
        </Typography>
        {onHover && (
          <Box sx={{ cursor: 'pointer', alignContent: 'center' }}>
            <Icon
              data-testid={`${dataTestId}-UploadFile-RemoveButton`}
              sx={{ color: 'deepOrange.900', fontSize: '18px' }}
              icon={Icons.delete}
              onClick={() => onClick(file.id)}
            />
          </Box>
        )}
      </UploadedFile>
    </Tooltip>
  );
};

export const UploadFiles: FC<{ uploadedFiles: FileAttachments[] }> = ({
  uploadedFiles,
}) => {
  const { removeFile } = useWizardContextV3();
  const handleOnDelete = (fileId) => removeFile(fileId);

  const files = uploadedFiles.map((file) => {
    return (
      <SelectedFile
        key={`${file.fileName}-${file.id}`}
        data-testid={`${dataTestId}-tooltip-${file.fileName}`}
        file={file}
        onClick={handleOnDelete}
      />
    );
  });

  if (files.length > 0) {
    return (
      <Stack direction="row" spacing="16px" sx={{ my: '8px' }}>
        {files}
      </Stack>
    );
  }
  return null;
};

export const UploadComponent: FC<{
  onFileFormats?: (boolean) => void;
  uploadedFiles: FileAttachments[];
  attachmentType: AttachmentType;
}> = ({ onFileFormats, uploadedFiles, attachmentType }) => {
  const { fileErrors, handleFileSelected } = useWizardContextV3();

  const [isFileLimit, setIsFileLimit] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    const isLimit = uploadedFiles?.length > 4;

    setIsFileLimit(isLimit);
  }, [uploadedFiles]);

  const errors = useMemo(() => {
    if (fileErrors.length > 0) {
      const result = fileErrors.map((error) => (
        <Typography key={error} color="error.main">
          {error}
        </Typography>
      ));
      return <>{result}</>;
    }
    return null;
  }, [fileErrors]);

  const handleClick = () => {
    inputRef.current?.click();
  };

  const handleInput = (e) => {
    e?.preventDefault();
    const file = e.target?.files[0];

    // clear out any select files.  This allows
    // the user to select the same file again to upload.
    if (e.target?.files) {
      e.target.value = '';
    }

    handleFileSelected(file, attachmentType);
  };

  return (
    <UploadGrid
      container
      flexDirection="row"
      alignContent="center"
      justifyContent="space-between"
    >
      <UploadFiles uploadedFiles={uploadedFiles} />
      <Stack
        width="100%"
        direction="row"
        alignItems="center"
        justifyContent="space-between"
      >
        <Subtitle3Typo>
          Supported formats
          <IconButton
            data-testid={`${dataTestId}-UploadFile-InfoIcon`}
            onClick={() => onFileFormats?.(true)}
            aria-label="supported-formats-tooltip"
          >
            <StyledIcon icon={Icons.info} />
          </IconButton>
        </Subtitle3Typo>
        <Subtitle3Typo>Maximum number of files: 5</Subtitle3Typo>
        <Subtitle3Typo>Maximum file size: 10 Mb</Subtitle3Typo>
        <Typography variant="body2" fontWeight="bold" color="#0B1029">
          Uploaded files: {uploadedFiles.length} out of 5
        </Typography>
        <Button
          variant="text"
          color="primary"
          disabled={isFileLimit}
          onClick={handleClick}
          startIcon={
            <StyledIcon
              icon={Icons.upload}
              sx={{
                fill: (theme) =>
                  isFileLimit
                    ? 'rgb(0, 95, 236, 24%)'
                    : theme.palette.primary.main,
              }}
            />
          }
          data-testid={`${dataTestId}-Support-HelpIcon`}
        >
          Upload
          <VisuallyHiddenInput
            ref={inputRef}
            data-testid={`${dataTestId}-Hidden-input`}
            type="file"
            onChange={handleInput}
          />
        </Button>
      </Stack>
      {errors}
    </UploadGrid>
  );
};
